import { useState, useEffect } from 'react';
import { configService } from '@/services/configService';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';

/**
 * Custom hook to build the feedback collector URL with all required parameters.
 * 
 * @param feature - The feature name to collect feedback for (e.g., "Homepage", "Timesheet")
 * @returns The complete URL for the oc-component, or null if config is not loaded yet
 */
export function useFeedbackCollectorUrl(feature: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const { currentEmployee } = useTimesheet();
  const { currentCompany } = useCompany();

  useEffect(() => {
    let isMounted = true;

    async function buildUrl() {
      try {
        const config = await configService.getFeedbackCollectorConfig();
        
        if (!isMounted) return;

        // Build URL with all parameters
        const params = new URLSearchParams();
        params.set('product', config.product);
        params.set('feature', feature);
        params.set('accountToken', config.accountToken);
        params.set('customerName', currentCompany?.name || 'Unknown');
        params.set('userId', currentEmployee?.id !== undefined ? String(currentEmployee.id) : currentEmployee?.name || 'Unknown');

        const fullUrl = `${config.baseUrl}?${params.toString()}`;
        setUrl(fullUrl);
      } catch (error) {
        console.error('Failed to build feedback collector URL:', error);
        setUrl(null);
      }
    }

    buildUrl();

    return () => {
      isMounted = false;
    };
  }, [feature, currentEmployee, currentCompany]);

  return url;
}
